import { matchPath } from "react-router-dom";
import { CompanyType } from "src/modules/company/types/Company";
import { UserProfilePermissionsModel } from "src/modules/profile/types/UserProfile";
import { UserRoles } from "src/modules/user/types/UserRoles";
import {
    PATH_ACCOUNT_BILLING,
    PATH_CONFIGURATION,
    PATH_CUSTOMERS,
    PATH_CUSTOMERS_ADD,
    PATH_CUSTOMERS_EDIT,
    PATH_CUSTOMER_AKA_USER_ADD,
    PATH_CUSTOMER_AKA_USER_EDIT,
    PATH_CUSTOMER_GROUPS,
    PATH_CUSTOMER_GROUPS_ADD,
    PATH_CUSTOMER_GROUPS_EDIT,
    PATH_EMPLOYEE,
    PATH_EMPLOYEE_ADD,
    PATH_EMPLOYEE_EDIT,
    PATH_LOCATIONS,
    PATH_LOCATIONS_ADD,
    PATH_LOCATIONS_EDIT,
    PATH_SALES_PAYOFF,
    PATH_SALES_SESSIONS,
    PATH_STATIONS,
    PATH_STATIONS_ADD,
    PATH_STATIONS_EDIT,
    PATH_STATION_DETAILS,
    PATH_SYSTEM_LOGS,
    PATH_TARIFFS,
    PATH_TARIFFS_ADD,
    PATH_TARIFFS_COPY,
    PATH_TARIFFS_EDIT,
    PATH_USERS,
    PATH_USERS_ADD,
    PATH_USERS_EDIT,
} from "./paths";

type RoutePermissionRule = {
    paths: string[];
    canAccess: (permissions: UserProfilePermissionsModel) => boolean;
};

export const hasAccess = (
    permissions: UserProfilePermissionsModel | null | undefined,
    role?: UserRoles,
    excludedCompanyType?: CompanyType
) => {
    if (!permissions) {
        return false;
    }

    if (excludedCompanyType && permissions.companyType === excludedCompanyType) {
        return false;
    }

    if (permissions.hasFullAccess) {
        return true;
    }

    if (!role) {
        return true;
    }

    return !!permissions.roles?.includes(role);
};

const routePermissionRules: RoutePermissionRule[] = [
    {
        paths: [PATH_SALES_SESSIONS],
        canAccess: (permissions) => hasAccess(permissions, UserRoles.ROLE_CAN_VIEW_SESSIONS),
    },
    {
        paths: [PATH_SALES_PAYOFF],
        canAccess: (permissions) =>
            hasAccess(permissions, UserRoles.ROLE_CAN_MANAGE_PAYOFFS, CompanyType.HOME),
    },
    {
        paths: [PATH_TARIFFS, PATH_TARIFFS_ADD, PATH_TARIFFS_COPY, PATH_TARIFFS_EDIT],
        canAccess: (permissions) =>
            hasAccess(permissions, UserRoles.ROLE_CAN_MANAGE_TARIFFS, CompanyType.HOME),
    },
    {
        paths: [PATH_CUSTOMERS, PATH_CUSTOMERS_ADD, PATH_CUSTOMERS_EDIT],
        canAccess: (permissions) =>
            hasAccess(permissions, UserRoles.ROLE_CAN_MANAGE_CUSTOMERS, CompanyType.HOME),
    },
    {
        paths: [PATH_CUSTOMER_GROUPS, PATH_CUSTOMER_GROUPS_ADD, PATH_CUSTOMER_GROUPS_EDIT],
        canAccess: (permissions) =>
            hasAccess(permissions, UserRoles.ROLE_CAN_MANAGE_CUSTOMERS, CompanyType.HOME),
    },
    {
        paths: [PATH_EMPLOYEE, PATH_EMPLOYEE_ADD, PATH_EMPLOYEE_EDIT],
        canAccess: (permissions) =>
            hasAccess(permissions, UserRoles.ROLE_CAN_MANAGE_EMPLOYEES, CompanyType.HOME),
    },
    {
        paths: [PATH_USERS, PATH_USERS_ADD, PATH_USERS_EDIT],
        canAccess: (permissions) => {
            if (permissions.companyType === CompanyType.HOME) {
                return true;
            }

            return hasAccess(permissions, UserRoles.ROLE_CAN_MANAGE_USERS);
        },
    },
    {
        paths: [PATH_CUSTOMER_AKA_USER_ADD, PATH_CUSTOMER_AKA_USER_EDIT],
        canAccess: (permissions) => permissions.companyType === CompanyType.HOME,
    },
    {
        paths: [PATH_CONFIGURATION, PATH_ACCOUNT_BILLING],
        canAccess: (permissions) => hasAccess(permissions, UserRoles.ROLE_CAN_MANAGE_INVOICES),
    },
    {
        paths: [PATH_SYSTEM_LOGS],
        canAccess: (permissions) =>
            hasAccess(permissions, UserRoles.ROLE_CAN_ACCESS_SYSTEM_LOGS, CompanyType.HOME),
    },
    {
        paths: [PATH_LOCATIONS, PATH_LOCATIONS_ADD, PATH_LOCATIONS_EDIT],
        canAccess: (permissions) =>
            hasAccess(permissions, UserRoles.ROLE_CAN_MANAGE_LOCATIONS, CompanyType.HOME),
    },
    {
        paths: [PATH_STATIONS, PATH_STATIONS_ADD, PATH_STATIONS_EDIT, PATH_STATION_DETAILS],
        canAccess: (permissions) => hasAccess(permissions, UserRoles.ROLE_CAN_MANAGE_STATIONS),
    },
];

export const hasRouteAccess = (
    pathname: string,
    permissions: UserProfilePermissionsModel | null | undefined
) => {
    if (!permissions) {
        return false;
    }

    const matchedRule = routePermissionRules.find((rule) =>
        rule.paths.some((path) => !!matchPath({ path, end: true }, pathname))
    );

    if (!matchedRule) {
        return true;
    }

    return matchedRule.canAccess(permissions);
};
